// MACHINERY V2 (Stroje) — Diely a materiál tab (M2.5).
//
// Spec: data package §5 + ticket §5 #5 / §7 fix 5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-parts.jsx (`PartsTab`,
// `PartsStates`, `PartsAddDialog`); artboards 1009 (list + bulk bar), 1010
// (skeleton / empty / no-results / the ADDED unlink error), 1011 (add dialog),
// 1013 (phone). Rows = partsRows.tsx (>= 768) + PartPhoneCard.tsx (< 768) over
// one partsRowContract.ts, query + selection = usePartsTabQuery.ts,
// mutations = usePartsTabActions.ts (300-line cap).
//
// IDENTICAL FOR CATALOG AND CUSTOM MACHINES. §5 catalog-vs-custom: the component
// takes only `machineId`. There is no `catalogModelId` anywhere in this file and
// there must not be one.
//
// THE LIST IS A BARE ARRAY, NOT A PAGE. §5: `GET /v1/supplier/installed-stock-
// items` returns `InstalledStockItemStatsViewModel[]` — no totalCount, no
// pagination UI, no tab badge. The count line is `length`.
//
// SEARCH / CATEGORY / BRAND STAY SERVER-SIDE, exactly as v1 sends them; only the
// SELECTION is client state. Search covers number OR alternativeNumber — that
// is the server's rule, not ours, so nothing is re-implemented here.
//
// TWO REVIEW FIXES LIVE NEXT DOOR, IN usePartsTabQuery.ts, AND THE STATES BELOW
// DEPEND ON BOTH: the machineId identity guard (R1 — `list` is empty unless the
// app-wide `installedStockItems` slot provably holds THIS machine's response, so
// the previous machine's parts can never be rendered, counted or unlinked here),
// and the selection prune plus membership-based `allSelected` (C1 — a tick may
// never outlive the row it points at). `ready` below IS that guard; do not
// replace it with a bare `!!items` test.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, V2_PHONE, useV2MediaQuery } from "../../useV2MediaQuery";
import { InstalledStockItem } from "../../../../inventory/models/models";
import { FleetMenuAction } from "../../fleet/FleetCardMenu";
import RetryNotice from "../../feedback/RetryNotice";
import { TabCard, TabEmpty, TabIntro } from "./tabAtoms";
import { SkeletonList } from "./tabSkeletons";
import PartPhoneCard from "./PartPhoneCard";
import { PartRow } from "./partsRows";
import { PartsAddCta, PartsBulkBar, PartsHeadRow, PartsToolbar } from "./partsTabChrome";
import { usePartsTabQuery } from "./usePartsTabQuery";
import { usePartsTabActions } from "./usePartsTabActions";
import PartsAddDialogV2 from "./PartsAddDialogV2";

export interface PartsTabV2Props {
  machineId: string;
}

const PartsTabV2: React.FC<PartsTabV2Props> = ({ machineId }) => {
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  // F-A: the module's ONE phone predicate, evaluated as the query the CSS
  // evaluates. Identical to `innerWidth < 768` at every integer width.
  const isPhone = useV2MediaQuery(V2_PHONE);
  // TOUCH IS A WIDER TIER THAN PHONE, AND THAT IS THE POINT. The list only
  // swaps to cards at 768, so the 768-1279 TABLET tier paints the desktop grid
  // row — including its 16px select box — on a touch device. Both hooks are
  // called unconditionally on their own lines: composing them behind a
  // short-circuit would be a conditional hook call.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

  const categories = useAppSelector((s) => s.customerMachine.categories);
  const manufacturers = useAppSelector((s) => s.customerMachine.manufacturers);

  const {
    query,
    search,
    list,
    ready,
    loading,
    failed,
    selected,
    allSelected,
    fetchWith,
    reload,
    handleSearch,
    handleSort,
    clearSearch,
    toggleAll,
    toggleOne,
    clearSelection,
  } = usePartsTabQuery(machineId);

  const [addOpen, setAddOpen] = useState(false);
  const { unlinkFailure, requestUnlink, retryUnlink, install, busy } = usePartsTabActions(
    machineId,
    reload,
    clearSelection
  );

  const hasFilter = !!query.search || !!query.categoryId || !!query.manufacturerId;
  const menuLabel = translate("myMachinery:v2.tabs.parts.rowMenu");
  const selectLabel = translate("myMachinery:v2.tabs.parts.selectRow");

  const buildActions = (item: InstalledStockItem): FleetMenuAction[] => [
    {
      id: "marketplace",
      labelKey: "myMachinery:findInMarketplace",
      onSelect: () => navigate(`/search?productNumber=${item.stockItem?.product?.number}`),
    },
    {
      id: "unlink",
      labelKey: "myMachinery:unlinkFromMachine",
      danger: true,
      onSelect: () =>
        requestUnlink([item.id], item.stockItem?.product?.number || "", true),
    },
  ];

  const rowProps = (item: InstalledStockItem) => ({
    item,
    selected: selected.indexOf(item.id) >= 0,
    onToggleSelect: () => toggleOne(item.id),
    actions: buildActions(item),
    onMarketplace: () => navigate(`/search?productNumber=${item.stockItem?.product?.number}`),
    menuLabel,
    selectLabel,
    touch: belowDesktop,
  });

  const showSkeleton = !ready && !failed;
  const showEmpty = ready && !loading && !failed && list.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <TabIntro
        text={translate("myMachinery:toolsPartsLinked")}
        count={translate("myMachinery:v2.tabs.parts.count", { count: list.length })}
      />

      <PartsToolbar
        search={search}
        onSearchChange={handleSearch}
        query={query}
        categories={categories || []}
        manufacturers={manufacturers || []}
        onQueryChange={fetchWith}
        onAdd={() => setAddOpen(true)}
        phone={isPhone}
      />

      {selected.length > 0 && (
        <PartsBulkBar
          count={selected.length}
          busy={busy}
          onUnlink={() =>
            requestUnlink(
              selected,
              String(selected.length),
              false
            )
          }
          onClear={clearSelection}
        />
      )}

      {failed && (
        <RetryNotice
          title={translate("myMachinery:v2.tabs.parts.loadFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={reload}
          busy={loading}
        />
      )}

      {unlinkFailure && (
        <RetryNotice
          title={translate(
            unlinkFailure.single
              ? "myMachinery:v2.tabs.parts.unlinkFailedOne"
              : "myMachinery:v2.tabs.parts.unlinkFailedMany",
            { name: unlinkFailure.label, count: unlinkFailure.ids.length }
          )}
          hint={translate("myMachinery:v2.tabs.parts.unlinkFailedHint")}
          onRetry={retryUnlink}
          busy={busy}
        />
      )}

      {showSkeleton && (
        <TabCard>
          <SkeletonList rows={3} height={46} />
        </TabCard>
      )}

      {showEmpty && (
        <TabEmpty
          title={
            hasFilter
              ? translate("myMachinery:v2.tabs.parts.noResultsTitle")
              : translate("myMachinery:noItemsAssociated")
          }
          hint={
            hasFilter
              ? translate("myMachinery:v2.tabs.parts.noResultsHint")
              : translate("myMachinery:v2.tabs.parts.emptyHint")
          }
          // §5: the Add button appears ONLY when no filter is active.
          ctaLabel={hasFilter ? undefined : translate("myMachinery:addNewItem")}
          onCta={hasFilter ? undefined : () => setAddOpen(true)}
          clearLabel={hasFilter ? translate("myMachinery:v2.tabs.clearFilters") : undefined}
          onClear={hasFilter ? clearSearch : undefined}
        />
      )}

      {list.length > 0 &&
        (isPhone ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            {list.map((item) => (
              <PartPhoneCard key={item.id} {...rowProps(item)} />
            ))}
          </div>
        ) : (
          <TabCard overflow="visible">
            <PartsHeadRow
              touch={belowDesktop}
              query={query}
              allSelected={allSelected}
              someSelected={selected.length > 0}
              onToggleAll={toggleAll}
              onSort={handleSort}
            />
            {list.map((item) => (
              <PartRow key={item.id} {...rowProps(item)} />
            ))}
          </TabCard>
        ))}

      {/* Board 24: on a phone the Add CTA is a full-width button UNDER the
          list, not a chip in the toolbar (board 24 drops it from the toolbar
          entirely — partsTabChrome.tsx's phone branch).

          >>> IT IS GATED ON THE ONE STATE THAT ALREADY OFFERS AN ADD, NOT ON
          THE LIST BEING NON-EMPTY (M2.16d review, item 7). <<< `list.length > 0`
          deleted the ONLY phone Add affordance in three states the desktop
          keeps it in — loading, load-failed, and filtered-with-no-results —
          because the toolbar has no Add on a phone and TabEmpty only offers one
          when NO filter is active (§5). The single state that must not show it
          is that unfiltered empty one, where TabEmpty's own CTA would make two
          stacked Add buttons — the duplication §5 avoids. So that is what the
          condition says now, literally. */}
      {isPhone && !(showEmpty && !hasFilter) && (
        <PartsAddCta onAdd={() => setAddOpen(true)} />
      )}

      <PartsAddDialogV2
        machineId={machineId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onConfirm={install}
      />
    </div>
  );
};

export default PartsTabV2;
